<script lang="ts">
	// Svelte Store
	import { writable } from 'svelte/store';
	// Svelte
	import { setContext, createEventDispatcher, onMount } from 'svelte';
	// Helpers
	import {
		setWizardSteps,
		updateWizardStepOnWizardItem,
		updateWizardStepOnPrevNext,
		scrollToElement,
	} from '../../../../../Lists/list-helpers';
	import { format } from 'svelte-i18n';
	import { is_time_based_cond } from '../../../../../../types/TimeBasedConditionsUI';
	import { user_settings } from 'src/stores/account/user';
	import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import {
		fahrenheit_to_celsius,
		real_id,
		tmp_units_from_settings,
	} from 'src/helpers/interface_helpers';
	import { TEMPERATURE_SENSORS, TEMPERATURE_SENSORS_IN_CELSIUS } from 'src/constants';
	import type { Device } from 'src/types/Device';
	// Design System
	import {
		Button,
		Wizard,
		WizardItem,
		Container,
		WizardContainer,
		WizardContent,
		Modal,
		toast,
	} from '@Shelly/shelly-design-system';
	// Components
	import ConditionStep from './Steps/ConditionStep.svelte';
	import DeviceStep from './Steps/DeviceBased/DeviceStep.svelte';
	import DevicePropsStep from './Steps/DeviceBased/DevicePropsStep.svelte';
	import WatchPropStep from './Steps/DeviceBased/WatchPropStep.svelte';
	import TimeStep from './Steps/TimeBased/TimeStep.svelte';
	import EnergyStep from './Steps/EnergyBased/EnergyStep.svelte';
	import SceneStep from './Steps/SceneBased/SceneStep.svelte';
	import AlarmStep from './Steps/AlarmBased/AlarmStep.svelte';
	import TimeSunriseSunsetStep from './Steps/SunriseSunsetBased/TimeSunriseSunsetStep.svelte';
	import PredictionStep from './Steps/WeatherBased/PredictionStep.svelte';
	import WeatherStep from './Steps/WeatherBased/WeatherStep.svelte';
	import WeatherExecutionTypeStep from './Steps/WeatherBased/WeatherExecutionTypeStep.svelte';
	import { getTranslationType } from './helpers/when-wizard-helpers';
	import ExitConfirmationModal from 'src/components/ExitConfirmationModal.svelte';
	import { deepCopy } from 'src/helpers/utilities';
	import MathStep from './Steps/MathBased/MathStep.svelte';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import type { ISceneObj } from 'src/types/SceneType';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let rawSceneObject: ISceneObj;
	export let sceneData: any = {};
	export let showWizard = false;
	export let stopConfirmationModal = false;
	export let backdropClose = false;
	export let showConfirmationModal = false;

	const dispatch = createEventDispatcher();
	const previousButton = $format('button_back');

	let wizardItemsContainerRef: HTMLElement = null;
	let showExitConfirmationModal = false;
	let exitConfirmationModalState = false;
	let clonedSceneData;
	let clonedSceneDataIsSet = false;

	let containersRefsObj = writable({
		wizardContainerRef: null,
		scrollContainerRef: null,
	});

	setContext('addConditionContainersRefs', containersRefsObj);

	let mappedWizardStepsComponents = {
		1: {
			0: ConditionStep,
			1: DeviceStep,
			2: DevicePropsStep,
			3: WatchPropStep,
		},
		2: {
			0: ConditionStep,
			1: TimeStep,
		},
		3: {
			0: ConditionStep,
			1: PredictionStep,
			2: WeatherStep,
			3: WeatherExecutionTypeStep,
		},
		4: {
			0: ConditionStep,
			1: TimeSunriseSunsetStep,
		},
		5: {
			0: ConditionStep,
			1: SceneStep,
		},
		6: {
			0: ConditionStep,
			1: EnergyStep,
		},
		7: {
			0: ConditionStep,
			1: AlarmStep,
		},
		8: {
			0: ConditionStep,
			1: MathStep,
		},
		9: {
			0: ConditionStep,
		},
	};

	const wizardStepsVariants = {
		1: [
			$format('wizard_step_condition', { default: 'Condition' }),
			$format('wizard_step_device', { default: 'Device' }),
			$format('wizard_step_device_prop', { default: 'Device properties' }),
			$format('wizard_step_watch_prop', { default: 'Watch property as' }),
		],
		2: [
			$format('wizard_step_condition', { default: 'Condition' }),
			$format('wizard_step_time_prop', { default: 'Time based properties' }),
		],
		3: [
			$format('wizard_step_condition', { default: 'Condition' }),
			$format('wizard_step_prediction_prop', { default: 'Prediction period' }),
			$format('wizard_step_weather', { default: 'Weather' }),
			$format('wizard_step_weather_exec_type', { default: 'Condition type' }),
		],
		4: [
			$format('wizard_step_condition', { default: 'Condition' }),
			$format('wizard_step_time', { default: 'Time' }),
		],
		5: [
			$format('wizard_step_condition', { default: 'Condition' }),
			$format('wizard_step_automation_prop', { default: 'Select scene' }),
		],
		6: [
			$format('wizard_step_condition', { default: 'Condition' }),
			$format('wizard_step_exchange_prop', { default: 'Exchange' }),
		],
		7: [
			$format('wizard_step_condition', { default: 'Condition' }),
			$format('wizard_step_alarm_prop', { default: 'Select alarm' }),
		],
		8: [
			$format('wizard_step_condition', { default: 'Condition' }),
			$format('wizard_step_math', { default: 'Math expression' }),
		],
		9: [$format('wizard_step_condition', { default: 'Condition' })],
	};

	// TODO add translations
	let wizardItems = setWizardSteps(wizardStepsVariants[sceneData?.selectedCondition]);

	$: wizardItems = setWizardSteps(
		wizardStepsVariants[sceneData?.selectedCondition],
		activeWizardStep.activeIndex,
	);

	let activeWizardStep = wizardItems?.[0];
	let modalRef: Modal = null;
	let scrolledToView = false;
	let firstSelectedRef: HTMLElement = null;

	$: isLastStepWizard = activeWizardStep.activeIndex === wizardItems.length - 1;
	$: nextButton = isLastStepWizard
		? $format('button_save', { default: 'Save' })
		: $format('button_next', { default: 'Next' });
	$: editedDeviceId = sceneData.translated?.device?.id
		? real_id(sceneData.translated.device.id)
		: undefined;
	$: hasMissingDevice =
		sceneData.when_info.mode === 'edit' &&
		sceneData.selectedCondition === 1 &&
		Boolean(editedDeviceId && !$devices[editedDeviceId]);

	const handleSaveTimeBased = () => {
		const { _time_trigger_obj } = sceneData;
		if (is_time_based_cond(_time_trigger_obj)) {
			dispatch('save', {
				t_data: _time_trigger_obj,
				s_type: 'time_trigger',
			});
		} else {
			toast.open({
				type: 'danger',
				title: $format('scene_invalid_format_err_toast'),
			});
		}
	};

	const handleSaveDeviceBased = () => {
		if (import.meta.env.DEV) {
			console.log('%c"handleSaveDeviceBased" called', 'font-size: 16px');
		}
		const { selectedWatchType, watchProp, when_info } = sceneData;
		let { _device_condition } = sceneData;

		if (import.meta.env.DEV) {
			console.log('%c_device_condition', 'color: #f00', _device_condition);
		}

		// Convert temperature from Fahrenheit to Celsius before dispatching
		let deviceData: Device = $devices[_device_condition.device.id];
		let deviceSettings = readDeviceSettings(real_id(_device_condition.device.id));
		const ALL_TEMPERATURE_SENSORS = [
			...new Set([...TEMPERATURE_SENSORS, ...TEMPERATURE_SENSORS_IN_CELSIUS]),
		];
		const tempUnit = tmp_units_from_settings(
			!TEMPERATURE_SENSORS_IN_CELSIUS.includes(deviceData?.type) ? deviceSettings : deviceData,
		);
		if (
			(ALL_TEMPERATURE_SENSORS.includes(deviceData?.type) ||
				typeof _device_condition?.add_info_ind !== 'undefined') &&
			(_device_condition.cond_name === 'temperature' ||
				_device_condition.cond_name === 'trv_target_temperature')
		) {
			_device_condition = {
				..._device_condition,
				value: tempUnit.includes('F')
					? fahrenheit_to_celsius(Number(_device_condition.value)).toString()
					: _device_condition.value,
			};
		}

		dispatch('save', {
			t_data: _device_condition,
			s_type: selectedWatchType,
			c_obj: selectedWatchType === 'trigger' ? watchProp : null,
			//TODO check do we need this. In old implementation it was passed aways on save dispatch
			when_info,
		});
	};

	const handleSaveEnergy = () => {
		const { active_condition } = sceneData;
		dispatch('save', {
			t_data: active_condition,
			s_type: 'exchange_based',
		});
	};

	const handleSaveSceneAlarmBased = () => {
		const { _automation_condition } = sceneData;
		dispatch('save', {
			t_data: _automation_condition,
			s_type: 'automation',
		});
	};

	const handleSaveSunsetSunriseBased = () => {
		const { _sunrise_sunset_cond } = sceneData;
		dispatch('save', {
			t_data: {
				..._sunrise_sunset_cond,
				locid: $user_settings?.weather_station?.l,
			},
			s_type: 'sun_trigger',
		});
	};

	const handleSaveWeatherBased = () => {
		const { weatherCondition } = sceneData;
		dispatch('save', {
			t_data: {
				...weatherCondition,
				locid: $user_settings?.weather_station?.l,
			},
			s_type: 'weather_trigger',
		});
	};

	const handleSaveMathBased = () => {
		const { _math_condition } = sceneData;
		dispatch('save', {
			t_data: {
				..._math_condition,
			},
			s_type: 'math_trigger',
		});
	};

	const handleSaveManualExecution = () => {
		dispatch('save', {
			t_data: {},
			s_type: 'manual_execution',
		});
	};

	const mappedSaveFunctions = {
		1: handleSaveDeviceBased,
		2: handleSaveTimeBased,
		3: handleSaveWeatherBased,
		4: handleSaveSunsetSunriseBased,
		5: handleSaveSceneAlarmBased,
		6: handleSaveEnergy,
		7: handleSaveSceneAlarmBased,
		8: handleSaveMathBased,
		9: handleSaveManualExecution,
	};

	const handleSave = () => {
		mappedSaveFunctions[sceneData.selectedCondition]();
	};

	//#regionstart wizard functions
	const onClickWizardItem = (index: number) => {
		const result = updateWizardStepOnWizardItem(index, wizardItems, activeWizardStep);
		wizardItems = result.wizardItems;
		activeWizardStep = { ...result.activeWizardStep };

		scrollSelectedCardToView();
	};

	const onClickPrevNextButton = (type: 'prev' | 'next') => {
		const result = updateWizardStepOnPrevNext(type, wizardItems, activeWizardStep);
		wizardItems = result.wizardItems;
		activeWizardStep = { ...result.activeWizardStep };

		scrollSelectedCardToView();
	};
	//#regionend wizard functions

	const isWizardStepDisabled = (wizardItem, i) => {
		const isEditMode = sceneData.when_info.mode === 'edit';
		const { isValid } = getTranslationType(sceneData.selectedCondition, sceneData);

		if (hasMissingDevice) {
			return !wizardItem.active && i > 1;
		}

		// Not disabled all on edit mode and only for the type that have translation
		if (isEditMode && isValid && sceneData.isValid) {
			return false;
		}

		// Not disabled second step. First step have default value
		if (activeWizardStep.activeIndex === 0 && i === 1) {
			return false;
		}

		// Not disabled next step when is valid
		if (sceneData.isValid && sceneData.validStepIndex + 1 === i && !wizardItem.active) {
			return false;
		}

		// Not disabled active wizard step
		if (wizardItem.active) {
			return false;
		}
		// Disable all wizard items witch are bigger than last valid step
		return wizardItem.activeIndex > sceneData.validStepIndex;
	};

	const isNextButtonDisabled = (sceneData, activeWizardStep) => {
		const isEditMode = sceneData.when_info.mode === 'edit';
		const { isValid } = getTranslationType(sceneData.selectedCondition, sceneData);

		if (hasMissingDevice && activeWizardStep.activeIndex >= 1) return true;

		// Not disabled all on edit mode and only for the type that have translation
		if (sceneData.isValid && isEditMode && isValid) return false;

		// Not disabled when we is not valid, but last valid index is biger than current active. Is back clicked and edit.
		if (!sceneData.isValid && activeWizardStep.activeIndex < sceneData.validStepIndex) return false;

		// Not disabled on first step
		if (activeWizardStep.activeIndex === 0) return false;

		// Not disabled on last step for Device based
		if (
			isLastStepWizard &&
			sceneData.isValid &&
			sceneData.selectedCondition === 1 &&
			(typeof sceneData.watchProp.valid_for_min === 'number' ||
				(!!sceneData.watchProp.valid_for_min && sceneData.watchProp.valid_for_min >= 0))
		) {
			return false;
		}

		// Not disabled on last step for Weather based
		if (isLastStepWizard && sceneData.selectedCondition === 3) {
			return false;
		}

		// Not disabled all steps before last validated
		if (sceneData.isValid && activeWizardStep.activeIndex <= sceneData.validStepIndex) {
			return false;
		}

		// Disabled next button if not condition applied
		return true;
	};

	let removeContainerEventListener = () => {};

	const scrollSelectedCardToView = () => {
		removeContainerEventListener();

		scrolledToView = false;
		let elementRef = null;
		let wizardStepIndex = 0;

		if (activeWizardStep.activeIndex === 1 && firstSelectedRef && !scrolledToView) {
			wizardStepIndex = 1;
			elementRef = firstSelectedRef;
		} else {
			wizardStepIndex = activeWizardStep.activeIndex;
			elementRef = null;
		}

		if (!elementRef) {
			modalRef.scrollToTop();
		} else {
			removeContainerEventListener = scrollToElement(
				elementRef,
				wizardItemsContainerRef,
				activeWizardStep.activeIndex,
				wizardStepIndex,
			);
		}

		scrolledToView = true;
	};

	const showExitConfirmation = () => {
		if (showConfirmationModal && !stopConfirmationModal) {
			showExitConfirmationModal = true;
			exitConfirmationModalState = false;
		} else {
			onCloseWizard();
		}
	};

	const onCloseWizard = () => {
		dispatch('close');
	};

	onMount(() => {
		clonedSceneData = deepCopy(sceneData);
	});

	// Using this condition to check when components is set,
	// because this changes initial sceneData and this should become the new clonedSceneData.
	$: if (sceneData.components.length && !clonedSceneDataIsSet) {
		clonedSceneData = deepCopy(sceneData);
		clonedSceneDataIsSet = true;
	}

	$: showConfirmationModal = JSON.stringify(clonedSceneData) !== JSON.stringify(sceneData);
</script>

<Modal
	title={sceneData.when_info.mode === 'edit'
		? $format('title_scene_edit_condition', { default: 'Edit condition' })
		: $format('title_scene_create_condition', { default: 'Create condition' })}
	showModal={showWizard}
	{backdropClose}
	bind:scrollContainerRef={$containersRefsObj.scrollContainerRef}
	on:close={showExitConfirmation}
	bind:this={modalRef}
	removeScrollPadding
>
	<Wizard slot="toolbar">
		{#each wizardItems as item, i (i)}
			<WizardItem
				on:click={() => {
					onClickWizardItem(i);
				}}
				active={item.active}
				title={item.title}
				showMoreDots={item.showMoreDots}
				disabled={isWizardStepDisabled(item, i)}
				indicatorColor={$showNewLayout ? 'color-2' : undefined}
			/>
		{/each}
	</Wizard>

	<Container padding="0" slot="content">
		<WizardContainer
			activeStepIndex={activeWizardStep.activeIndex}
			adaptiveHeight
			bind:wizardContainerRef={$containersRefsObj.wizardContainerRef}
			bind:wizardItemsContainerRef
		>
			{#each wizardItems as _, i (i)}
				<!-- DO NOT REMOVE COMMENT: Padding references -->
				<WizardContent
					active={activeWizardStep.activeIndex === i}
					class={classMap({
						'pb-s': true,
						'pt-s':
							sceneData.selectedCondition === 1 ||
							sceneData.selectedCondition === 5 ||
							sceneData.selectedCondition === 7,
					})}
				>
					{#if activeWizardStep.activeIndex === i}
						<svelte:component
							this={mappedWizardStepsComponents[sceneData.selectedCondition][i]}
							{rawSceneObject}
							bind:sceneData
							bind:firstSelectedRef
							{...sceneData.selectedCondition === 1 && i === 2
								? sceneData.extractPropsFromSelectedCond
								: {}}
						/>
					{/if}
				</WizardContent>
			{/each}
		</WizardContainer>
	</Container>

	<div class="d-flex justify-content-end" slot="footer">
		{#if activeWizardStep.activeIndex > 0}
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				icon="fas fa-chevron-left"
				variant="secondary"
				responsiveSize={{
					sm: 'm',
				}}
				on:click={() => {
					onClickPrevNextButton('prev');
				}}
			>
				{previousButton}
			</Button>
		{/if}
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			icon={isLastStepWizard ? 'fas fa-save' : ''}
			rightIcon={isLastStepWizard ? '' : 'fas fa-chevron-right'}
			responsiveSize={{
				sm: 'm',
			}}
			disabled={isNextButtonDisabled(sceneData, activeWizardStep)}
			on:click={() => {
				if (isLastStepWizard) {
					handleSave();
				} else {
					onClickPrevNextButton('next');
				}
			}}
		>
			{nextButton}
		</Button>
	</div>
</Modal>

<ExitConfirmationModal
	type={$format('the_condition', { default: 'the condition' })}
	{exitConfirmationModalState}
	{showExitConfirmationModal}
	showAddEditModal={showWizard}
	on:close={(e) => {
		exitConfirmationModalState = e.detail.exitConfirmationModalState;
		showExitConfirmationModal = e.detail.showExitConfirmationModal;
	}}
	on:confirm={(e) => {
		showExitConfirmationModal = e.detail.showExitConfirmationModal;
		onCloseWizard();
	}}
/>
